import Card from "@mui/material/Card";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Rating from "@mui/material/Rating";
import CloseIcon from "@mui/icons-material/Close";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import FeedbackTableData from "Views/feedback/data/feedbackTableData";
import { useState, useEffect } from "react";
import Dropdown from "components/CustomDropdown/Dropdown";
import PaginationBar from "components/PaginationBar/PaginationBar";
import api from "utils/api";
import dayjs from "dayjs";
import {
  ADMIN_UNREAD_MODULES,
  markAdminUnreadSeen,
} from "services/adminUnreadService";

function Feedback() {
  const [typeFilter, setTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({});
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleViewFeedback = async (feedback) => {
    setSelectedFeedback(feedback);
    setViewDialog(true);

    if (!feedback?._id || !feedback.adminUnread) return;

    try {
      await markAdminUnreadSeen(ADMIN_UNREAD_MODULES.FEEDBACK, feedback._id);
      fetchFeedbacks();
    } catch (error) {
      console.error("Error marking feedback as seen:", error);
    }
  };

  const { columns, rows, loading, totalCount, fetchFeedbacks } = FeedbackTableData(
    filters,
    handleViewFeedback
  );

  const selectedUser = selectedFeedback?.parentId || selectedFeedback?.tutorId;

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = rows.slice(startIndex, endIndex);

  // Update sno for paginated rows
  const rowsWithCorrectSno = paginatedRows.map((row, index) => ({
    ...row,
    sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {startIndex + index + 1}
      </ArgonTypography>
    ),
  }));

  useEffect(() => {
    setFilters({
      type: typeFilter,
      page: currentPage,
      limit: rowsPerPage,
    });
    setCurrentPage(1); // Reset to first page when filters change
  }, [typeFilter]);

  useEffect(() => {
    setFilters({
      type: typeFilter,
      page: currentPage,
      limit: rowsPerPage,
    });
  }, [currentPage, rowsPerPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                All Feedbacks
              </ArgonTypography>

              <ArgonBox display="flex" alignItems="center" gap={2} mb={2}>
                <Dropdown
                  label="Filter by Type"
                  value={typeFilter}
                  options={["ALL", "PARENT", "TUTOR"]}
                  onChange={(e) => setTypeFilter(e.target.value === "ALL" ? "" : e.target.value)}
                  width={200}
                />
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2">Loading...</ArgonTypography>
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Feedback found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rowsWithCorrectSno} />
              )}

              {/* Pagination */}
              {rows.length > 0 && (
                <PaginationBar
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  totalItems={rows.length}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: "linear-gradient(135deg, #263c87 0%, #11cdef 100%)",
          }}
        >
          <ArgonBox p={2.5} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>
                Feedback Details
              </ArgonTypography>
              <ArgonTypography variant="caption" sx={{ color: "rgba(255,255,255,0.82)" }}>
                Review submitted by {selectedFeedback?.createdBy || "user"}
              </ArgonTypography>
            </ArgonBox>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              {selectedFeedback?.type && (
                <Chip
                  label={selectedFeedback.type}
                  size="small"
                  sx={{
                    color: "#0f172a",
                    backgroundColor: "#fff",
                    fontWeight: 800,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => setViewDialog(false)}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.16)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.26)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ArgonBox>
          </ArgonBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedFeedback && (
            <ArgonBox p={2.5} display="flex" flexDirection="column" gap={2.25}>
              <ArgonBox
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e9ecef",
                }}
              >
                <Avatar
                  src={selectedUser?.profileImage || undefined}
                  alt={selectedUser?.fullName || "User"}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: "#5e72e4",
                    color: "#fff",
                    fontWeight: 800,
                  }}
                >
                  {(selectedUser?.fullName || "U").charAt(0)}
                </Avatar>
                <ArgonBox minWidth={0}>
                  <ArgonTypography variant="button" color="text" fontWeight="bold">
                    {selectedUser?.fullName || "N/A"}
                  </ArgonTypography>
                  <ArgonTypography
                    variant="caption"
                    color="secondary"
                    sx={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {selectedUser?.email || "N/A"}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={1.5}
              >
                <ArgonBox
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    backgroundColor: "rgba(251, 176, 0, 0.08)",
                    border: "1px solid rgba(251, 176, 0, 0.25)",
                  }}
                >
                  <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                    Rating
                  </ArgonTypography>
                  <ArgonBox display="flex" alignItems="center" gap={1} mt={0.75}>
                    <Rating value={selectedFeedback.rating || 0} readOnly max={10} size="small" />
                    <ArgonTypography variant="h6" color="text" fontWeight="bold">
                      {selectedFeedback.rating}/10
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <ArgonBox
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                    Submitted
                  </ArgonTypography>
                  <ArgonTypography variant="button" color="text" fontWeight="bold" display="block" mt={0.75}>
                    {selectedFeedback.createdBy || "N/A"}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {dayjs(selectedFeedback.createdAt).format("DD MMM YYYY, hh:mm A")}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox>
                <ArgonTypography variant="button" color="text" fontWeight="bold">
                  Description
                </ArgonTypography>
                <ArgonBox
                  mt={1}
                  sx={{
                    p: 1.75,
                    maxHeight: 180,
                    overflow: "auto",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    border: "1px solid #e9ecef",
                    boxShadow: "inset 0 1px 0 rgba(15, 23, 42, 0.03)",
                  }}
                >
                  <ArgonTypography
                    variant="body2"
                    color="text"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.7,
                    }}
                  >
                    {selectedFeedback.description}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.75, backgroundColor: "#f8fafc" }}>
          <ArgonButton
            color="info"
            variant="gradient"
            onClick={() => setViewDialog(false)}
            sx={{ minWidth: 110, borderRadius: "10px" }}
          >
            Close
          </ArgonButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Feedback;

